/*
 * Copyright (c) 2024-2026. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import React, {useMemo, useState} from 'react'
import clsx from 'clsx'
import {XMarkIcon} from '@heroicons/react/24/solid'
import {useTranslation} from 'react-i18next'
import {useDebouncedCallback} from 'use-debounce'
import {RANGE_FILTER_INPUT_BASE_CLASS} from 'components/Ui/rangeFilterClassNames'

interface UiNumberRangeFilterProps {
    value: { from?: number; to?: number }
    onChange: (value: { from?: number; to?: number } | undefined) => void
    placeholder?: string
    debounceTime?: number
    maxValue?: number
    maxDigits?: number
}

const UiNumberRangeFilter: React.FC<UiNumberRangeFilterProps> = ({
    value,
    onChange,
    placeholder = 'from - to',
    debounceTime = 300,
    maxValue,
    maxDigits,
}) => {
    const { t } = useTranslation()
    const [fromValue, setFromValue] = useState<string>(value?.from?.toString() ?? '')
    const [toValue, setToValue] = useState<string>(value?.to?.toString() ?? '')
    const [isDirty, setIsDirty] = useState(false)

    const [fromPlaceholder, toPlaceholder] = useMemo(() => {
        const [fromLabel = '', toLabel = ''] = placeholder
            .split('-')
            .map((part) => part.trim())

        return [fromLabel || 'from', toLabel || 'to']
    }, [placeholder])

    const externalFromValue = value?.from?.toString() ?? ''
    const externalToValue = value?.to?.toString() ?? ''
    const effectiveFromValue = isDirty ? fromValue : externalFromValue
    const effectiveToValue = isDirty ? toValue : externalToValue

    const emitChange = (fromRaw: string, toRaw: string) => {
        const fromTrimmed = fromRaw.trim()
        const toTrimmed = toRaw.trim()

        let fromNum: number | undefined
        let toNum: number | undefined

        if (fromTrimmed) {
            const parsed = parseInt(fromTrimmed, 10)
            if (!isNaN(parsed) && isFinite(parsed)) {
                fromNum = parsed
            }
        }

        if (toTrimmed) {
            const parsed = parseInt(toTrimmed, 10)
            if (!isNaN(parsed) && isFinite(parsed)) {
                toNum = parsed
            }
        }

        if (fromNum === undefined && toNum === undefined) {
            onChange(undefined)
            return
        }

        const safeFrom =
            fromNum !== undefined && maxValue !== undefined
                ? Math.min(fromNum, maxValue)
                : fromNum
        const safeTo =
            toNum !== undefined && maxValue !== undefined
                ? Math.min(toNum, maxValue)
                : toNum

        onChange({
            from: safeFrom,
            to: safeTo,
        })
    }

    const debouncedEmitChange = useDebouncedCallback(
        (fromRaw: string, toRaw: string) => emitChange(fromRaw, toRaw),
        debounceTime
    )

    const normalizeValue = (nextValue: string): string => {
        if (!nextValue) return ''

        const digitsOnly = nextValue.replace(/\D/g, '')
        if (!digitsOnly) return ''

        const limitedByDigits = maxDigits
            ? digitsOnly.slice(0, maxDigits)
            : digitsOnly
        const parsed = parseInt(limitedByDigits, 10)

        if (!isNaN(parsed) && isFinite(parsed) && maxValue !== undefined) {
            return String(Math.min(parsed, maxValue))
        }

        return limitedByDigits
    }

    const handleFromChange = (newValue: string) => {
        const normalized = normalizeValue(newValue)
        setFromValue(normalized)
        setIsDirty(true)
        debouncedEmitChange(normalized, effectiveToValue)
    }

    const handleToChange = (newValue: string) => {
        const normalized = normalizeValue(newValue)
        setToValue(normalized)
        setIsDirty(true)
        debouncedEmitChange(effectiveFromValue, normalized)
    }

    const handleApply = () => {
        debouncedEmitChange.cancel()
        emitChange(effectiveFromValue, effectiveToValue)
    }

    const handleClear = () => {
        debouncedEmitChange.cancel()
        setFromValue('')
        setToValue('')
        onChange(undefined)
        setIsDirty(false)
    }

    const hasValue = effectiveFromValue || effectiveToValue

    return (
        <div
            className={clsx(
                'UiNumberRangeFilter flex flex-row gap-0.5 items-center justify-start w-full max-w-xs relative bg-white/50 border border-gray-300/80 rounded-md transition-all duration-150 focus-within:ring-1 focus-within:ring-blue-400 focus-within:bg-white dark:bg-gray-900/50 dark:border-gray-700/80 dark:focus-within:bg-gray-900 font-normal mt-1.5 px-1.5 py-[5px]',
                'min-w-0'
            )}
            data-debounce-time={debounceTime}
            onClick={(e) => e.stopPropagation()}
        >
            <input
                className={clsx(
					'number-input-no-spinner',
					RANGE_FILTER_INPUT_BASE_CLASS,
                    fromValue ? 'text-blue-500 font-semibold' : 'text-gray-700 dark:text-gray-100'
                )}
                placeholder={t(fromPlaceholder)}
                type="number"
                value={effectiveFromValue}
                onChange={(e) => handleFromChange(e.target.value)}
                min="0"
                max={maxValue}
            />
            <span className="text-gray-400 text-xs shrink-0">-</span>
            <input
                className={clsx(
                    'number-input-no-spinner',
                    RANGE_FILTER_INPUT_BASE_CLASS,
                    toValue ? 'text-blue-500 font-semibold' : 'text-gray-700 dark:text-gray-100'
                )}
                placeholder={t(toPlaceholder)}
                type="number"
                value={effectiveToValue}
                onChange={(e) => handleToChange(e.target.value)}
                min="0"
                max={maxValue}
            />
            {hasValue && (
                <button
                    className={clsx(
						'flex items-center justify-center px-1 py-[1px] rounded-md transition-all duration-150 flex-none',
                        'bg-gray-200 text-gray-700 hover:bg-gray-300 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600'
                    )}
                    onClick={handleClear}
                    type="button"
                    title={t('Clear')}
                >
                    <XMarkIcon className="w-3.5 h-3.5" />
                </button>
            )}
            {/*<button*/}
            {/*    className={clsx(*/}
            {/*        'flex items-center justify-center px-1.5 py-0.5 rounded-md transition-all duration-150 flex-none ml-0.5',*/}
            {/*        effectiveFromValue || effectiveToValue*/}
            {/*            ? 'bg-blue-500 text-white hover:bg-blue-600 dark:bg-blue-600 dark:hover:bg-blue-700 cursor-pointer'*/}
            {/*            : 'bg-gray-300 text-gray-400 dark:bg-gray-700 dark:text-gray-600 cursor-not-allowed'*/}
            {/*    )}*/}
            {/*    onClick={handleApply}*/}
            {/*    type="button"*/}
            {/*    disabled={!effectiveFromValue && !effectiveToValue}*/}
            {/*    title={t('Apply filter')}*/}
            {/*>*/}
            {/*    <CheckIcon className="w-3.5 h-3.5" />*/}
            {/*</button>*/}
        </div>
    )
}

export default UiNumberRangeFilter
